'use server'

import { headers } from 'next/headers'
import { resolveTenant } from './tenant'
import { getPayloadClient } from './payload'
import { subscribeEmail } from './newsletterOps'
import { clientIp } from './rateLimit'

/**
 * Server action del form di iscrizione. E' il pattern che questo repo usa già per far parlare un
 * client component col server (vedi src/lib/streamActions.ts), e qui porta due garanzie che un
 * endpoint pubblico avrebbe dovuto ricostruire a mano:
 *
 *  - IL TENANT NON VIAGGIA DAL CLIENT. Lo risolve `resolveTenant()` dall'Host, come ogni pagina:
 *    nessun campo del form può dirottare l'iscrizione su un'altra testata della piattaforma.
 *  - LE SERVER ACTION DI NEXT SONO SAME-ORIGIN per costruzione (controllo su Origin/Host), quindi
 *    un form ospitato su un altro dominio non le può invocare.
 *
 * La risposta è SEMPRE la stessa frase per tutti gli esiti positivi: iscritto nuovo, iscrizione da
 * riconfermare, indirizzo già in lista. Vedi subscribeEmail in src/lib/newsletterOps.ts.
 */

/** Le origini ammesse: `source` finisce in una colonna, quindi non si accetta testo libero dal client. */
const SOURCES = new Set(['footer', 'pagina', 'articolo'])

export type SubscribeState = {
  status: 'ok' | 'invalid' | 'disabled' | 'rate' | 'error'
  message: string
}

const MESSAGES: Record<SubscribeState['status'], string> = {
  ok: 'Ci siamo quasi: ti abbiamo mandato una email per confermare l\'indirizzo. Apri il link e l\'iscrizione è fatta.',
  invalid: 'Questo indirizzo non sembra un indirizzo email. Controlla e riprova.',
  disabled: 'Le iscrizioni sono chiuse in questo momento. Riprova più tardi.',
  rate: 'Hai già provato poco fa. Aspetta qualche minuto, poi riprova.',
  error: 'Non siamo riusciti a mandare l\'email di conferma. Riprova tra qualche minuto.',
}

export async function subscribeToNewsletter(input: {
  email: string
  source?: string
  /** Campo esca: un browser umano lo lascia vuoto perché non lo vede. */
  trap?: string
}): Promise<SubscribeState> {
  // Bot che compila tutti i campi del form: si risponde come a un successo, senza scrivere né
  // inviare niente. Dirgli "sei un bot" gli insegnerebbe quale campo saltare al prossimo giro.
  if (input.trap && input.trap.trim().length > 0) return { status: 'ok', message: MESSAGES.ok }

  const tenant = await resolveTenant()
  const payload = await getPayloadClient()
  const h = await headers()

  const result = await subscribeEmail({
    payload,
    tenant,
    email: input.email,
    source: input.source && SOURCES.has(input.source) ? input.source : 'footer',
    ip: clientIp(h),
    userAgent: h.get('user-agent') ?? undefined,
  })

  if (result.ok) return { status: 'ok', message: MESSAGES.ok }
  const status = result.error === 'send' ? 'error' : result.error
  return { status, message: MESSAGES[status] }
}

/**
 * La stessa cosa nella forma che vuole `useActionState`, cioè `(statoPrecedente, FormData)`.
 *
 * Esiste per una ragione precisa: passata così a `<form action={...}>`, l'iscrizione funziona
 * ANCHE SENZA JAVASCRIPT (Next fa la POST nativa e rirenderizza la pagina con l'esito). Un form
 * che senza JS resta muto è un form che perde iscritti, e non si vede in nessun test.
 */
export async function subscribeFormAction(_prev: SubscribeState | null, formData: FormData): Promise<SubscribeState> {
  return subscribeToNewsletter({
    email: String(formData.get('email') ?? ''),
    source: String(formData.get('source') ?? 'footer'),
    trap: String(formData.get('website') ?? ''),
  })
}
